import { Gavel, HeartHandshake, ScrollText, ShoppingBag, Banknote, Stethoscope, Briefcase, ArrowUpRight } from "lucide-react";

const areas = [
  {
    icon: Gavel,
    title: "Direito Penal",
    intro:
      "Atuação técnica e estratégica em investigações, inquéritos policiais, ações penais e procedimentos administrativos.",
    closing:
      "Defesa jurídica conduzida com discrição, precisão técnica e foco na proteção dos direitos e garantias do cliente.",
  },
  {
    icon: HeartHandshake,
    title: "Direito Previdenciário",
    bulletsIntro: "Assessoria especializada em:",
    items: [
      "Aposentadorias",
      "Benefícios do INSS",
      "Pensões",
      "Auxílios",
      "Revisões previdenciárias",
      "Planejamento previdenciário",
      "Demandas judiciais",
    ],
    closing:
      "Atuação consultiva e contenciosa voltada à maximização de direitos e segurança previdenciária.",
  },
  {
    icon: ScrollText,
    title: "Direito Civil",
    bulletsIntro: "Atuação em demandas envolvendo:",
    items: [
      "Contratos",
      "Responsabilidade civil",
      "Indenizações",
      "Obrigações",
      "Sucessões",
      "Cobranças",
      "Litígios patrimoniais",
    ],
    closing:
      "Soluções jurídicas estratégicas para prevenção e resolução eficiente de conflitos.",
  },
  {
    icon: ShoppingBag,
    title: "Direito do Consumidor",
    bulletsIntro: "Defesa de consumidores e empresas em conflitos relacionados a:",
    items: [
      "Produtos e serviços",
      "Cobranças indevidas",
      "Negativação",
      "Relações contratuais",
      "Responsabilidade civil",
    ],
    closing:
      "Atuação voltada à mitigação de riscos e proteção dos direitos envolvidos.",
  },
  {
    icon: Banknote,
    title: "Direito Bancário",
    bulletsIntro:
      "Assessoria jurídica em demandas envolvendo instituições financeiras, incluindo:",
    items: [
      "Revisão contratual",
      "Cobranças abusivas",
      "Fraudes bancárias",
      "Empréstimos e financiamentos",
      "Contratos bancários",
    ],
    closing:
      "Foco na proteção patrimonial e no equilíbrio das relações financeiras.",
  },
  {
    icon: Stethoscope,
    title: "Direito Médico",
    intro:
      "Atuação especializada para profissionais da saúde, clínicas, hospitais e pacientes.",
    bulletsIntro: "O escritório oferece assessoria em:",
    items: [
      "Responsabilidade civil médica",
      "Processos ético-disciplinares",
      "Judicialização da saúde",
      "Defesa em ações indenizatórias",
      "Elaboração e revisão de contratos",
      "Consultoria preventiva e regulatória",
    ],
    closing:
      "Uma atuação estratégica voltada à mitigação de riscos e segurança jurídica na área da saúde.",
  },
  {
    icon: Briefcase,
    title: "Gestão de Afastados e NR-1",
    intro:
      "Consultoria estratégica para empresas na gestão jurídica de empregados afastados e adequação às exigências da NR-1.",
    bulletsIntro: "Atuação focada em:",
    items: [
      "Redução de passivos trabalhistas e previdenciários",
      "Gestão de riscos psicossociais",
      "Protocolos preventivos",
      "Auditorias jurídicas",
      "Adequação regulatória",
      "Treinamento e suporte à gestão",
    ],
    closing:
      "Uma abordagem preventiva que fortalece a governança corporativa e reduz riscos futuros.",
    wide: true,
  },
];

export default function AreasDeAtuacao() {
  return (
    <section id="areas-de-atuacao" className="section bg-brand-soft" data-testid="section-areas">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-3xl mb-16">
          <h2 className="font-serif text-brand-primary font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Áreas de <em className="text-brand-accent not-italic">Atuação</em>.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {areas.map(({ icon: Icon, title, intro, bulletsIntro, items, closing, wide }) => (
            <article
              key={title}
              className={`area-card bg-white border-l-2 border-brand-primary/15 p-8 lg:p-10 group ${
                wide ? "lg:col-span-3" : ""
              }`}
              data-testid={`area-card-${title.toLowerCase().replace(/\s/g, "-")}`}
            >
              <div className="flex items-start justify-between">
                <div className="w-12 h-12 flex items-center justify-center bg-brand-primary text-brand-accent">
                  <Icon size={22} strokeWidth={1.4} />
                </div>
                <ArrowUpRight size={18} className="text-brand-primary/30 group-hover:text-brand-accent transition-colors" />
              </div>
              <h3 className="font-serif text-brand-primary text-2xl lg:text-3xl mt-6 leading-tight">
                {title}
              </h3>

              {intro && (
                <p className="mt-4 text-[14px] leading-relaxed text-brand-text/80">{intro}</p>
              )}

              {bulletsIntro && (
                <p className="mt-4 text-[14px] leading-relaxed text-brand-text/80">{bulletsIntro}</p>
              )}

              {items && items.length > 0 && (
                <ul className={`mt-3 ${wide ? "grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8" : ""} space-y-2 text-[13px] text-brand-primary/85`}>
                  {items.map((it) => (
                    <li key={it} className="flex items-start gap-2">
                      <span className="block w-1 h-1 bg-brand-accent mt-2 flex-shrink-0" />
                      {it}
                    </li>
                  ))}
                </ul>
              )}

              {closing && (
                <p className="mt-5 text-[13px] leading-relaxed text-brand-text/70 italic">
                  {closing}
                </p>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
